// frontend/src/pages/canvasser/CallBatchScreen.tsx
import { useMemo, useRef, useState } from 'react'
import { Alert, Box, HStack, Spinner, Text, VStack } from '@chakra-ui/react'
import { Navigate, useSearchParams } from 'react-router-dom'
import { toaster } from '@/components/ui/toaster'
import {
  callToast, useCallOverlay, useCallScript, useGrantCallBatch, usePendingCalls, usePrefetchCallScripts, useRecordCall,
} from '../../hooks/useCanvasserCalls'
import { useGrantCampaign } from '../../hooks/useGrantCampaign'
import { useToday } from '../../hooks/useToday'
import { useSync } from '../../providers/SyncProvider'
import { isCacheExpiredError } from '../../lib/offlineStore'
import { clearPath } from '../../lib/conversation'
import { openableCards } from '../../lib/todayAssignments'
import { readScriptLanguage } from '../../lib/walkPrefs'
import { uuid } from '../../lib/uuid'
import { CacheExpiredNotice } from '../../components/canvasser/CacheExpiredNotice'
import { TodayBackLink } from '../../components/canvasser/TodayBackLink'
import { CallRow, isMarkable } from '../../components/canvasser/calls/CallRow'
import { CallOutcomeSheet, type CallOutcomeChoice } from '../../components/canvasser/calls/CallOutcomeSheet'
import { CallScriptSheet, callConversationKey, type CallScriptResult } from '../../components/canvasser/calls/CallScriptSheet'
import type { CallBatchItemRowDto, RecordCallSubmission } from '../../types/followUp'

const NEEDS_CONNECTION = 'This script needs a connection. You can still record an outcome.'

interface ActiveCall {
  row: CallBatchItemRowDto
  /** Minted once when the sheet opens; every retry of this call reuses it (server idempotency). */
  clientCallId: string
  phase: 'script' | 'outcome'
  script: CallScriptResult | null
  notice: string | null
}

/**
 * [CF-1 P4 §7.6] The canvasser's call list, opened by the home's CallBatch branch (or the
 * `call-batch?callBatchId=` deep link). The batch id is a prop, never a path param; no campaignId in the
 * URL. Calls are recorded through `useRecordCall` (queued offline in `pendingCalls`), never as visits.
 */
export function CallBatchScreen({ callBatchId, showTodayLink }: { callBatchId: string; showTodayLink: boolean }) {
  const { isOnline, pendingCount } = useSync()
  // Until the post-mount read settles, `data` may be the pre-drain copy and the overlay is empty: no Mark outcome.
  const { data, isLoading, error, isFetchedAfterMount } = useGrantCallBatch(callBatchId)
  const pending = usePendingCalls(callBatchId, pendingCount)
  usePrefetchCallScripts(callBatchId, data?.items, isOnline)
  const record = useRecordCall().record
  const [active, setActive] = useState<ActiveCall | null>(null)
  const [saving, setSaving] = useState(false)
  const savingRef = useRef(false)
  const [language] = useState(readScriptLanguage)
  const script = useCallScript(callBatchId, active?.row.itemId, active?.phase === 'script')

  const byItem = useCallOverlay(callBatchId, pending, data?.items)
  const rows = useMemo(() => {
    const items = data?.items ?? []
    const rank = (r: CallBatchItemRowDto) => (isMarkable(r, byItem.get(r.itemId)?.entry) ? 0 : r.status === 'Open' ? 1 : 2)
    return items.map((r, i) => ({ r, i })).sort((a, b) => rank(a.r) - rank(b.r) || a.i - b.i).map(x => x.r)
  }, [data, byItem])
  const done = rows.filter(r => r.calledAt != null || (byItem.has(r.itemId) && !byItem.get(r.itemId)!.entry.rejected)).length

  const open = (row: CallBatchItemRowDto) =>
    setActive({ row, clientCallId: uuid(), phase: row.scriptId ? 'script' : 'outcome', script: null, notice: null })

  const submit = async (choice: CallOutcomeChoice) => {
    if (!active || savingRef.current) return
    const a = active
    const call: RecordCallSubmission = {
      clientCallId: a.clientCallId,
      itemId: a.row.itemId,
      outcome: choice.outcome,
      scriptId: a.script?.scriptId ?? null,
      clientTimestamp: new Date().toISOString(),
      ...(a.script ? {
        surveyResponses: a.script.surveyResponses,
        promptResponses: a.script.promptResponses,
        tagAssignments: a.script.tagAssignments,
      } : {}),
      note: choice.note || undefined,
      markDoNotContact: choice.markDoNotContact,
    }
    savingRef.current = true
    setSaving(true)
    try {
      const result = await record(callBatchId, call)
      toaster.create(callToast(result))
      clearPath(callConversationKey(callBatchId, a.row.itemId))
      setActive(null)
    } catch {
      // Keep the sheet (and its clientCallId) so Save retries the same call.
      toaster.create({ type: 'error', title: "Couldn't save this call. Try again." })
    } finally {
      savingRef.current = false
      setSaving(false)
    }
  }

  if (isLoading) return <VStack py={12}><Spinner size="lg" color="accent" /></VStack>
  if (error) {
    if (isCacheExpiredError(error)) return <CacheExpiredNotice />
    return (
      <Box p={4}>
        <Alert.Root status="error" borderRadius="md"><Alert.Indicator />We couldn't load this call list. Try again in a moment.</Alert.Root>
      </Box>
    )
  }
  if (!data) return null

  return (
    <VStack align="stretch" gap={3} px={4} py={3} pb="calc(16px + env(safe-area-inset-bottom, 0px))">
      <HStack gap={2} mx={showTodayLink ? -2 : 0}>
        {showTodayLink && <TodayBackLink />}
        <Text as="h1" fontWeight="bold" fontSize="lg" flex={1} minW={0} lineClamp={2}>{data.batch.label}</Text>
        <Text fontSize="sm" color="fg.muted" flexShrink={0} pr={showTodayLink ? 2 : 0}>{`${done} of ${rows.length} called`}</Text>
      </HStack>

      {rows.length === 0 && <Text color="fg.muted" textAlign="center" py={8}>Nobody is on this call list.</Text>}
      {rows.map(r => (
        <CallRow key={r.itemId} row={r} local={byItem.get(r.itemId)} canMark={isFetchedAfterMount} onMarkOutcome={() => open(r)} />
      ))}

      {active && (
        <CallScriptSheet
          open={active.phase === 'script'}
          batchId={callBatchId} itemId={active.row.itemId} name={active.row.displayName}
          script={script.data} isLoading={script.isLoading} isError={script.isError} language={language}
          onFinish={r => setActive({ ...active, phase: 'outcome', script: r, notice: null })}
          onUnavailable={() => setActive({
            ...active, phase: 'outcome', script: null,
            // Offline / not cached: say so. Online with no script resolved: just the quick outcome.
            notice: script.isError || !isOnline ? NEEDS_CONNECTION : null,
          })}
          onClose={() => setActive(null)} />
      )}
      {active && (
        <CallOutcomeSheet
          key={active.clientCallId}
          open={active.phase === 'outcome'}
          name={active.row.displayName}
          notice={active.notice}
          saving={saving}
          // × after a conversation goes back to it (never discards the answers); otherwise closes.
          onClose={() => setActive(active.script ? { ...active, phase: 'script', script: null } : null)}
          onSubmit={c => void submit(c)} />
      )}
    </VStack>
  )
}

/**
 * `/canvasser/call-batch?callBatchId=` — the deep link (P3's action-route precedent). The home normally
 * renders the screen inline; this also reaches a cached batch offline, when Today has no call cards.
 */
export function CallBatchRoute() {
  const [params] = useSearchParams()
  const callBatchId = params.get('callBatchId')
  const campaign = useGrantCampaign()
  const today = useToday(campaign?.id)
  if (!callBatchId) return <Navigate to="/canvasser" replace />
  return <CallBatchScreen key={callBatchId} callBatchId={callBatchId} showTodayLink={openableCards(today.cards).length > 1} />
}
